import React from 'react';

import { Trans } from '@lingui/macro';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import Grid from '@mui/material/Grid';
import Icon from '@mui/icons-material/CloudUpload';
import PeopleIcon from '@mui/icons-material/People';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import Typography from '@mui/material/Typography';

import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import BoxTextarea from '../../../../misc/BoxTextarea';
import Textarea from '../../../../misc/Textarea';

function Source(props) {
	const channelid    = props.config?.channelid || 'external';
	const configWhip   = props.config?.whip || {};

	// null  = still loading
	// false = WHIP disabled or Core returned an error
	// object = { base_publish_url, base_whep_url, has_token, ... }
	const [whipInfo,     setWhipInfo]     = React.useState(null);
	const [obsUrl,       setObsUrl]       = React.useState('');
	const [whepUrl,      setWhepUrl]      = React.useState('');
	const [bearerToken,  setBearerToken]  = React.useState('');
	const [subscribers,  setSubscribers]  = React.useState(0);
	const [active,       setActive]       = React.useState(false);

	// WHEP preview state
	const [previewActive, setPreviewActive] = React.useState(false);
	const [previewError,  setPreviewError]  = React.useState('');
	const pcRef        = React.useRef(null);
	const locationRef  = React.useRef(null);
	const videoRef     = React.useRef(null);
	const streamRef    = React.useRef(null); // MediaStream across re-renders

	// ── 1. Load live WHIP state from Core ──────────────────────────────────
	const loadWhipInfo = React.useCallback(async () => {
		const fromSessionConfig = () =>
			configWhip.enabled === true
				? { base_publish_url: `http://${configWhip.host}/whip/`, has_token: !!configWhip.token }
				: false;

		if (props.restreamer?.WhipUrl) {
			const info = await props.restreamer.WhipUrl();
			setWhipInfo(info !== null ? info : fromSessionConfig());
		} else {
			setWhipInfo(fromSessionConfig());
		}
	}, [props.restreamer, configWhip.enabled, configWhip.host, configWhip.token]);

	React.useEffect(() => {
		loadWhipInfo();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// ── 2. Build OBS + WHEP URLs ────────────────────────────────────────────
	React.useEffect(() => {
		if (!whipInfo) {
			setObsUrl('');
			setWhepUrl('');
			setBearerToken('');
			return;
		}
		if (props.restreamer?.WhipStreamUrl) {
			props.restreamer.WhipStreamUrl(channelid).then((data) => {
				if (data?.publish_url) {
					setObsUrl(data.publish_url.split('?')[0]);
				} else {
					const base = whipInfo.base_publish_url || `http://${configWhip.host}/whip/`;
					setObsUrl(`${base}${channelid}`);
				}
				if (data?.whep_url) {
					setWhepUrl(data.whep_url);
				} else {
					const base = whipInfo.base_whep_url || whipInfo.base_publish_url || `http://${configWhip.host}/whip/`;
					setWhepUrl(`${base}${channelid}/whep`);
				}
				setBearerToken(configWhip.token || '');
			});
		} else {
			const base = whipInfo.base_publish_url || `http://${configWhip.host}/whip/`;
			setObsUrl(`${base}${channelid}`);
			setWhepUrl(`${base}${channelid}/whep`);
			setBearerToken(configWhip.token || '');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [whipInfo, channelid]);

	// ── 3. Poll active streams + subscriber count ───────────────────────────
	const pollWhipStreams = React.useCallback(async () => {
		if (!props.restreamer) return;
		try {
			const streams = await props.restreamer.WhipStreams();
			const stream  = streams.find((s) => s.name === channelid);
			setActive(!!stream);
			setSubscribers(stream?.subscribers ?? 0);
		} catch (_) {}
	}, [props.restreamer, channelid]);

	React.useEffect(() => {
		if (!whipInfo || !props.restreamer) return;
		pollWhipStreams();
		const id = setInterval(pollWhipStreams, 5000);
		return () => clearInterval(id);
	}, [whipInfo, pollWhipStreams, props.restreamer]);

	const rtspAvailable = configWhip.rtspEnabled === true;
	const [mode, setMode] = React.useState('direct');

	// ── 4. Notify parent ───────────────────────────────────────────────────
	React.useEffect(() => {
		if (whipInfo === null) return;
		const enabled = !!whipInfo;
		const addr   = mode === 'rtsp' ? `{whip-rtsp:name=${channelid}}` : `{whip:name=${channelid}}`;
		const inputs  = [{ address: addr, options: [] }];
		props.onChange('network', { mode: 'push', push: { type: 'whip', name: channelid } }, inputs, enabled);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [whipInfo, channelid, mode]);

	// ── 5. WHEP preview ────────────────────────────────────────────────────
	const startPreview = React.useCallback(async () => {
		setPreviewError('');
		try {
			const pc = new RTCPeerConnection({ iceServers: [] });

			// ontrack BEFORE createOffer — tracks fire as soon as ICE+DTLS completes
			pc.ontrack = (e) => {
				if (!e.streams?.[0]) return;
				streamRef.current = e.streams[0];
				if (videoRef.current) {
					videoRef.current.srcObject = e.streams[0];
					videoRef.current.play().catch(() => {});
				}
			};

			pc.addTransceiver('video', { direction: 'recvonly' });
			pc.addTransceiver('audio', { direction: 'recvonly' });

			const offer = await pc.createOffer();
			await pc.setLocalDescription(offer);

			// Wait for ICE gathering to complete (max 4s) before POSTing
			if (pc.iceGatheringState !== 'complete') {
				await new Promise((resolve) => {
					const tid = setTimeout(resolve, 4000);
					const handler = () => {
						if (pc.iceGatheringState === 'complete') {
							clearTimeout(tid);
							pc.removeEventListener('icegatheringstatechange', handler);
							resolve();
						}
					};
					pc.addEventListener('icegatheringstatechange', handler);
				});
			}

			const headers = { 'Content-Type': 'application/sdp' };
			if (bearerToken) headers['Authorization'] = `Bearer ${bearerToken}`;

			// Use pc.localDescription.sdp — now contains gathered ICE candidates
			const resp = await fetch(whepUrl, { method: 'POST', headers, body: pc.localDescription.sdp });
			if (!resp.ok) {
				throw new Error(`WHEP ${resp.status}`);
			}

			locationRef.current = resp.headers.get('Location');
			const answerSDP = await resp.text();
			await pc.setRemoteDescription({ type: 'answer', sdp: answerSDP });

			pcRef.current = pc;
			setPreviewActive(true);
		} catch (err) {
			setPreviewError(err.message);
		}
	}, [whepUrl, bearerToken]);

	// Apply stream after previewActive→true causes re-render and videoRef becomes available
	React.useEffect(() => {
		if (previewActive && videoRef.current && streamRef.current) {
			videoRef.current.srcObject = streamRef.current;
			videoRef.current.play().catch(() => {});
		}
	}, [previewActive]);

	const stopPreview = React.useCallback(async () => {
		if (pcRef.current) {
			pcRef.current.close();
			pcRef.current = null;
		}
		if (locationRef.current && whepUrl) {
			const base = whepUrl.replace(/\/whep$/, '');
			const deleteUrl = base + locationRef.current;
			try { await fetch(deleteUrl, { method: 'DELETE' }); } catch (_) {}
			locationRef.current = null;
		}
		if (videoRef.current) {
			videoRef.current.srcObject = null;
		}
		setPreviewActive(false);
	}, [whepUrl]);

	// Stop preview on unmount
	React.useEffect(() => {
		return () => { if (pcRef.current) { pcRef.current.close(); } };
	}, []);

	// ── Render ─────────────────────────────────────────────────────────────
	if (whipInfo === null) return null;

	if (!whipInfo) {
		return (
			<Grid item xs={12}>
				<Typography>
					<Trans>WHIP server is not enabled. Enable it in Settings → WHIP Server.</Trans>
				</Typography>
			</Grid>
		);
	}

	return (
		<React.Fragment>
			<Grid item xs={12}>
				<Typography variant="caption" style={{ opacity: 0.7 }}>
					<Trans>FFmpeg access mode</Trans>
				</Typography>
				<RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)}>
					<FormControlLabel
						value="direct"
						control={<Radio size="small" />}
						label={<Typography variant="body2"><Trans>Direct — single process</Trans></Typography>}
					/>
					<FormControlLabel
						value="rtsp"
						control={<Radio size="small" />}
						disabled={!rtspAvailable}
						label={<Typography variant="body2"><Trans>RTSP relay — multiple processes</Trans></Typography>}
					/>
				</RadioGroup>
				{mode === 'rtsp' && !rtspAvailable && (
					<Typography variant="caption" color="error">
						<Trans>RTSP relay not configured. Enable it in Settings → WHIP Server → Internal RTSP relay port.</Trans>
					</Typography>
				)}
			</Grid>
			<Grid item xs={12}>
				<Typography>
					<Trans>OBS → Settings → Stream → Service: WHIP</Trans>
				</Typography>
			</Grid>
			<Grid item xs={12}>
				<Typography variant="caption" style={{ opacity: 0.7 }}>
					<Trans>Server</Trans>
				</Typography>
				<BoxTextarea>
					<Textarea rows={1} value={obsUrl} readOnly allowCopy />
				</BoxTextarea>
			</Grid>
			{bearerToken && (
				<Grid item xs={12}>
					<Typography variant="caption" style={{ opacity: 0.7 }}>
						<Trans>Bearer Token</Trans>
					</Typography>
					<BoxTextarea>
						<Textarea rows={1} value={bearerToken} readOnly allowCopy />
					</BoxTextarea>
				</Grid>
			)}
			{props.restreamer && (
				<React.Fragment>
					<Grid item xs={12} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
						{active ? (
							<React.Fragment>
								<Chip
									icon={<FiberManualRecordIcon style={{ color: '#27ae60' }} />}
									label={<Trans>Live</Trans>}
									size="small"
									style={{ backgroundColor: 'rgba(39,174,96,0.15)', color: '#27ae60', border: '1px solid #27ae60' }}
								/>
								{subscribers > 0 && (
									<Chip
										icon={<PeopleIcon style={{ fontSize: 14 }} />}
										label={subscribers}
										size="small"
										style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)' }}
									/>
								)}
								{!previewActive ? (
									<Button
										size="small"
										variant="outlined"
										startIcon={<PlayArrowIcon />}
										onClick={startPreview}
										style={{ marginLeft: 'auto' }}
									>
										<Trans>Preview</Trans>
									</Button>
								) : (
									<Button
										size="small"
										variant="outlined"
										color="secondary"
										startIcon={<StopIcon />}
										onClick={stopPreview}
										style={{ marginLeft: 'auto' }}
									>
										<Trans>Stop preview</Trans>
									</Button>
								)}
							</React.Fragment>
						) : (
							<Chip
								icon={<FiberManualRecordIcon style={{ color: 'rgba(255,255,255,0.3)' }} />}
								label={<Trans>Waiting for publisher…</Trans>}
								size="small"
								style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)' }}
							/>
						)}
					</Grid>
					{previewError && (
						<Grid item xs={12}>
							<Typography variant="caption" color="error">{previewError}</Typography>
						</Grid>
					)}
					{/* Always render <video> while Live so videoRef is in DOM when ontrack fires */}
					{active && (
						<Grid item xs={12} style={{ display: previewActive ? 'block' : 'none' }}>
							<video
								ref={videoRef}
								autoPlay
								playsInline
								muted
								controls
								style={{ width: '100%', borderRadius: 4, background: '#000' }}
							/>
						</Grid>
					)}
				</React.Fragment>
			)}
		</React.Fragment>
	);
}

Source.defaultProps = {
	knownDevices: [],
	settings: {},
	config: null,
	skills: null,
	restreamer: null,
	onChange: function (type, settings, inputs, ready) {},
	onRefresh: function () {},
};

function SourceIcon(props) {
	return <Icon style={{ color: '#FFF' }} {...props} />;
}

const id           = 'whip';
const type         = 'network';
const name         = <Trans>WHIP server</Trans>;
const capabilities = ['audio', 'video'];

export { id, type, name, capabilities, SourceIcon as icon, Source as component };
